import { useState, useEffect, useCallback, useRef } from "react";
import { API, authFetch } from "@/App";
import AlbumCard from "@/components/AlbumCard";
import { Sparkles, Music, Users, TrendingUp, Loader2, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export default function Recommendations() {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all"); // "all", "genre", "similar_user", "trending"
  const [refreshKey, setRefreshKey] = useState(0); // Used to force new recommendations
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);

  useEffect(() => {
    fetchRecommendations();
  }, [refreshKey]);

  const fetchRecommendations = async () => {
    setLoading(true);
    try {
      // Add a random seed to get different results each time
      const seed = Math.random().toString(36).substring(7);
      const res = await authFetch(`${API}/recommendations?limit=30&seed=${seed}`);
      if (res.ok) {
        const data = await res.json();
        setRecommendations(data);
      }
    } catch (error) {
      console.error("Failed to fetch recommendations:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreRecommendations = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const seed = Math.random().toString(36).substring(7);
      const res = await authFetch(`${API}/recommendations?limit=24&seed=${seed}`);
      if (res.ok) {
        const data = await res.json();
        // Filter out duplicates
        const existingIds = new Set(recommendations.map(r => r.album_id || r.deezer_id));
        const newAlbums = data.filter(album => !existingIds.has(album.album_id || album.deezer_id));
        if (newAlbums.length > 0) {
          setRecommendations(prev => [...prev, ...newAlbums]);
        }
      }
    } catch (error) {
      console.error("Failed to load more recommendations:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (loading) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore) {
          loadMoreRecommendations();
        }
      },
      { threshold: 0.1 }
    );
    
    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }
    
    observerRef.current = observer;
    
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loading, loadingMore, recommendations]);

  const getRecommendationIcon = (type) => {
    switch (type) {
      case "genre":
        return <Music className="w-3 h-3" />;
      case "similar_user":
        return <Users className="w-3 h-3" />;
      case "trending":
        return <TrendingUp className="w-3 h-3" />;
      default:
        return <Sparkles className="w-3 h-3" />;
    }
  };

  const getRecommendationColor = (type) => {
    switch (type) {
      case "genre":
        return "bg-purple-500/20 text-purple-300 border-purple-500/30";
      case "similar_user":
        return "bg-blue-500/20 text-blue-300 border-blue-500/30";
      case "trending":
        return "bg-orange-500/20 text-orange-300 border-orange-500/30";
      default:
        return "bg-white/10 text-white/70 border-white/20";
    }
  };

  // Filter recommendations based on active filter
  const filteredRecommendations = activeFilter === "all" 
    ? recommendations 
    : recommendations.filter(album => album.recommendation_type === activeFilter);

  const filterCounts = {
    all: recommendations.length,
    genre: recommendations.filter(r => r.recommendation_type === "genre").length,
    similar_user: recommendations.filter(r => r.recommendation_type === "similar_user").length,
    trending: recommendations.filter(r => r.recommendation_type === "trending").length,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0510] pt-36">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0510] pt-36" data-testid="recommendations-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Sparkles className="w-8 h-8 text-purple-400" />
              <h1 className="text-3xl font-bold text-white">For You</h1>
            </div>
            <Button
              onClick={handleRefresh}
              disabled={loading}
              variant="outline"
              className="rounded-full border-white/20 text-white hover:bg-white/10 gap-2"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
          <p className="text-white/60">
            Albums we think you'll love, based on your taste and similar listeners
          </p>
        </div>

        {/* Filter Toggles */}
        <div className="flex flex-wrap gap-3 mb-8">
          <Badge 
            className={`cursor-pointer transition-all ${
              activeFilter === "all" 
                ? "bg-white/20 text-white border-white/50 ring-2 ring-white/30" 
                : "bg-white/5 text-white/60 border-white/20 hover:bg-white/10"
            }`}
            onClick={() => setActiveFilter("all")}
            data-testid="filter-all"
          >
            <Sparkles className="w-3 h-3 mr-1" />
            All ({filterCounts.all})
          </Badge>
          <Badge 
            className={`cursor-pointer transition-all ${
              activeFilter === "genre" 
                ? "bg-purple-500/40 text-purple-200 border-purple-400 ring-2 ring-purple-400/50" 
                : "bg-purple-500/20 text-purple-300 border-purple-500/30 hover:bg-purple-500/30"
            }`}
            onClick={() => setActiveFilter("genre")}
            data-testid="filter-genre"
          >
            <Music className="w-3 h-3 mr-1" />
            Your Genres ({filterCounts.genre})
          </Badge>
          <Badge 
            className={`cursor-pointer transition-all ${
              activeFilter === "similar_user" 
                ? "bg-blue-500/40 text-blue-200 border-blue-400 ring-2 ring-blue-400/50" 
                : "bg-blue-500/20 text-blue-300 border-blue-500/30 hover:bg-blue-500/30"
            }`}
            onClick={() => setActiveFilter("similar_user")}
            data-testid="filter-similar"
          >
            <Users className="w-3 h-3 mr-1" />
            Similar Listeners ({filterCounts.similar_user})
          </Badge>
          <Badge 
            className={`cursor-pointer transition-all ${
              activeFilter === "trending" 
                ? "bg-orange-500/40 text-orange-200 border-orange-400 ring-2 ring-orange-400/50" 
                : "bg-orange-500/20 text-orange-300 border-orange-500/30 hover:bg-orange-500/30"
            }`}
            onClick={() => setActiveFilter("trending")}
            data-testid="filter-trending"
          >
            <TrendingUp className="w-3 h-3 mr-1" />
            Trending ({filterCounts.trending})
          </Badge>
        </div>

        {/* Recommendations grid */}
        {filteredRecommendations.length > 0 ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
              {filteredRecommendations.map((album, index) => (
                <div key={`${album.album_id || album.deezer_id}-${index}`} className="space-y-2">
                  <AlbumCard album={album} compact />
                  {album.recommendation_reason && (
                    <Badge 
                      className={`text-xs truncate max-w-full ${getRecommendationColor(album.recommendation_type)}`}
                      title={album.recommendation_reason}
                    >
                      {getRecommendationIcon(album.recommendation_type)}
                      <span className="ml-1 truncate">{album.recommendation_reason}</span>
                    </Badge>
                  )}
                </div>
              ))}
            </div>
            
            {/* Load more trigger */}
            <div 
              ref={loadMoreRef} 
              className="flex justify-center py-12"
            >
              {loadingMore ? (
                <div className="flex items-center gap-2 text-white/60">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Loading more...</span>
                </div>
              ) : (
                <p className="text-white/40 text-sm">Scroll for more recommendations</p>
              )}
            </div>
          </>
        ) : (
          <div className="text-center py-16">
            <Sparkles className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">No recommendations yet</h2>
            <p className="text-white/50 mb-6">
              Start logging albums to get personalized recommendations!
            </p>
            <Button
              onClick={handleRefresh}
              variant="outline"
              className="rounded-full border-white/20 text-white hover:bg-white/10 gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
